import React, { useMemo } from 'react';
import { NewSuggestion, SlicedSuggestions } from 'types/suggestions';
import { SponsoredProduct } from 'types/sponsored-products';
import { NewSuggestionsList } from '../new-suggestions-list';
import { SponsoredProductsList } from '../sponsored-products-list';
import { useSuggestionsButtonAction } from '../../hooks/use-suggestions-button-action';
import {
  shouldSplitNewSuggestionsAroundSponsored,
  splitNewSuggestionsForSponsored,
} from '../utils/split-new-suggestions-for-sponsored';
import { SPONSORED_INSERT_AFTER_NEW_SUGGESTIONS } from '../utils/get-element-index';

type NewSuggestionsContentProps = {
  newSuggestions: NewSuggestion[];
  hasSponsoredProducts: boolean;
  showOnlyCategories: boolean;
  showProducts: boolean;
  suggestionSelector: string;
  isDefaultList: boolean;
  index: number;
  isSrp: boolean;
  isKzDomain: boolean;
  akinatorOffset: number;
  defaultListPinnedOffset: number;
  searchQuery: string;
  suggestions: SlicedSuggestions;
  sponsoredProducts: Array<SponsoredProduct & { contentId: string }>;
  buttonAction: ReturnType<typeof useSuggestionsButtonAction>;
};

export const NewSuggestionsContent: React.FC<NewSuggestionsContentProps> = ({
  newSuggestions,
  hasSponsoredProducts,
  showOnlyCategories,
  showProducts,
  suggestionSelector,
  isDefaultList,
  index,
  isSrp,
  isKzDomain,
  akinatorOffset,
  defaultListPinnedOffset,
  searchQuery,
  suggestions,
  sponsoredProducts,
  buttonAction,
}) => {
  const shouldSplit = shouldSplitNewSuggestionsAroundSponsored(hasSponsoredProducts, showOnlyCategories);

  const { leadingNewSuggestions, trailingNewSuggestions, trailingIndexOffset } = useMemo(
    () => splitNewSuggestionsForSponsored(newSuggestions, sponsoredProducts.length, shouldSplit),
    [newSuggestions, shouldSplit, sponsoredProducts.length],
  );

  const newSuggestionsListProps = {
    showProducts,
    suggestionSelector,
    isDefaultList,
    index,
    isSrp,
    buttonAction,
    showOnlyCategories,
    akinatorOffset,
    defaultListPinnedOffset,
  };

  const sponsoredProductsListProps = {
    isKzDomain,
    suggestionSelector,
    isDefaultList,
    index,
    isSrp,
    suggestions,
    searchQuery,
    sponsoredProducts,
    buttonAction,
    akinatorOffset,
    defaultListPinnedOffset,
  };

  if (!shouldSplit) {
    return <NewSuggestionsList {...newSuggestionsListProps} slicedNewSuggestions={leadingNewSuggestions} />;
  }

  return (
    <>
      <NewSuggestionsList {...newSuggestionsListProps} slicedNewSuggestions={leadingNewSuggestions} />
      <SponsoredProductsList
        {...sponsoredProductsListProps}
        withDivider
        newSuggestionsBeforeSponsored={SPONSORED_INSERT_AFTER_NEW_SUGGESTIONS}
      />
      <NewSuggestionsList
        {...newSuggestionsListProps}
        slicedNewSuggestions={trailingNewSuggestions}
        itemIndexOffset={trailingIndexOffset}
      />
    </>
  );
};
